// src/redux/slices/personalDashboardSlice.js
import { createSlice } from "@reduxjs/toolkit";
import {
  fetchWithToken,
  postWithToken,
  patchWithToken,
} from "../../config/apiRequests";
import { toast } from "react-toastify";
import { showSuccessToast } from "../../utils/toastUtils";
import { handleApiResponse } from "../../utils/reducerHelpers";
// import { updateRequestDetails } from './companyDashboardSlice'

// Small helper for simple GET-like reducers to avoid repeating the same block
const setFetchState = (state, action, { endpoint = "", destKey = null, successMessage = "" } = {}) => {
  state.endPoint = endpoint;
  state.isLoading = false;

  if (action.payload) {
    state.success = true;
    if (successMessage) state.message = successMessage;
    if (destKey) {
      state[destKey] = action.payload;
    }
  } else {
    state.success = false;
  }
};

export const personalDashboardSlice = createSlice({
  name: "personalDashboard",
  initialState: {
    teamName: "",
    teamId: "",
    isLoading: false,
    success: false,
    error: null,
    message: "",
    endPoint: "",
    historyData: {},
    servicesData: {
      services: [1, 2, 3, 4, 5, 6, 7, 8, 9],
    },
    employeesData: undefined,
    managerName: "",
    totalTeamCost: undefined,
    directTeamCost: undefined,
    totalSubmissions: "",
    totalFeedbacks: "",
    totalRequests: "",
    highCostServices: undefined,
    employeesmanagerData: {},
    submissionsData: {},
    requestsData: {},
    feedbacksData: {},
    feedbacksDetailsData: {},
    updatefeedbackData: {},
    submissionDetailsData: {},
    updaterequestsData: {},
    usageReportData: {},
    printingUsageReportData: {},
    teamPrintingUsageReportData: {},
    employeeCostHistory: {},
    requestDetailsData: {},
    categoriesData: {},
    allServicesData: {},
    updateStatusSubmission: {},
    updateSubmissionsData: {
      expensed_by_name: null,
      service_to_replace_id: null,
      submission_details: "",
    },
    employeeDetailData: undefined,
    savingOpportunityData: {},
    similarServiceData: {},
    createdSubmission: false,
    userExpenses: [],
    userExpensesComment: {},
    expensesCommentData: {},
    teamExpenses: [],
    updateStatusRequest: {},
    approvalRequestsData: {},
    updateApprovalRequestData: {},
    // keep requestCommentData, reCommentData etc.
    requestCommentData: {},
    reCommentData: {},
    notificationsData: {},
    markNotificationsData: {},
    emailEnabled: {},
  },

  reducers: {
    // ---- read-style reducers (use setFetchState)
    updateHistory: (state, action) =>
      setFetchState(state, action, {
        endpoint: "api/v1/personal/costs_history",
        destKey: "historyData",
        successMessage: "History fetched successfully",
      }),

    updateUsageReportHistory: (state, action) =>
      setFetchState(state, action, {
        endpoint: "api/v1/personal/services/:service_id/costs_history",
        destKey: "serviceHistoryData",
        successMessage: "History fetched successfully",
      }),

    updateServices: (state, action) =>
      setFetchState(state, action, {
        endpoint: "api/v1/personal/services",
        destKey: "servicesData",
        successMessage: "Services fetched successfully",
      }),

    updateSubmissions: (state, action) =>
      setFetchState(state, action, {
        endpoint: "api/v1/personal/submissions",
        destKey: "submissionsData",
        successMessage: "Submissions fetched successfully",
      }),

    updateSubmissionDetails: (state, action) =>
      setFetchState(state, action, {
        endpoint: "api/v1/personal/submissions/{id}",
        destKey: "submissionDetailsData",
        successMessage: "Submission details fetched successfully",
      }),

    updateNotifications: (state, action) =>
      setFetchState(state, action, {
        endpoint: "api/v1/personal/notifications",
        destKey: "notificationsData",
        successMessage: "Notifications fetched successfully",
      }),

    markAllReadNotification: (state, action) =>
      handleApiResponse(state, action, {
        endpoint: "api/v1/personal/notifications/mark_all_read",
        successMessage: "Notifications mark all read successfully",
        dataKey: "markNotificationsData",
        defaultFailMessage: "Notifications mark all read failed",
        successStatuses: [200, 201],
      }),

    markreadNotification: (state, action) =>
      handleApiResponse(state, action, {
        endpoint: "api/v1/personal/notifications/mark_read",
        // successMessage: "Notifications mark all read successfully",
        dataKey: "markNotificationsData",
        defaultFailMessage: "Notifications mark all read failed",
        successStatuses: [200, 201],
      }),


    userSettings: (state, action) =>
      setFetchState(state, action, {
        endpoint: "api/v1/user_settings",
        destKey: "emailEnabled",
      }),


    updateRequests: (state, action) =>
      setFetchState(state, action, {
        endpoint: "api/v1/personal/requests",
        destKey: "requestsData",
        successMessage: "Requests fetched successfully",
      }),

    updateFeedbacks: (state, action) =>
      setFetchState(state, action, {
        endpoint: "api/v1/personal/feedbacks",
        destKey: "feedbacksData",
        successMessage: "Feedbacks fetched successfully",
      }),

    updateFeedbackDetails: (state, action) =>
      setFetchState(state, action, {
        endpoint: "api/v1/personal/feedbacks/:id",
        destKey: "feedbacksDetailsData",
        successMessage: "Details Feedback fetched successfully",
      }),

    updateEmployeeDetail: (state, action) =>
      setFetchState(state, action, {
        endpoint: "api/v1/personal/employees/:id",
        destKey: "employeeDetailData",
        successMessage: "Employee details fetched successfully",
      }),

    updateCategories: (state, action) =>
      setFetchState(state, action, {
        endpoint: "api/v1/categories",
        destKey: "categoriesData",
        successMessage: "Requests fetched successfully",
      }),

    updateAllServices: (state, action) =>
      setFetchState(state, action, {
        endpoint: "api/v1/personal/services/all_services",
        destKey: "allServicesData",
        successMessage: "Requests fetched successfully",
      }),

    updateUsageReport: (state, action) => {
      state.error = null;
      setFetchState(state, action, {
        endpoint: "api/v1/personal/services/{id}/usage_report",
        destKey: "usageReportData",
        successMessage: "Usage report fetched successfully",
      });
    },

    printingUsageReport: (state, action) => {
      state.error = null;
      setFetchState(state, action, {
        endpoint: "api/v1/personal/services/{id}/printing_usage_report",
        destKey: "printingUsageReportData",
        successMessage: "Printing Usage report fetched successfully",
      });
    },

    teamPrintingUsageReport: (state, action) => {
      state.error = null;
      setFetchState(state, action, {
        endpoint: "/api/v1/personal/users/userId/team_printing_usage_report?service_id=serviceId",
        destKey: "teamPrintingUsageReportData",
        successMessage: "Printing Usage report fetched successfully",
      });
    },

    updateRequestDetails: (state, action) =>
      setFetchState(state, action, {
        endpoint: "api/v1/personal/requests/{id}",
        destKey: "requestDetailsData",
        successMessage: "Request details fetched successfully",
      }),

    updateEmployeeCostHistory: (state, action) =>
      setFetchState(state, action, {
        endpoint: "api/v1/employees/emp_id/costs_history",
        destKey: "employeeCostHistory",
        successMessage: "Employee's cost history fetched successfully",
      }),

    updateUserExpenses: (state, action) =>
      setFetchState(state, action, {
        endpoint: "api/v1/users/user_id/personal_expenses",
        destKey: "userExpenses",
        successMessage: "User expenses fetched successfully!",
      }),

    updateTeamExpenses: (state, action) =>
      setFetchState(state, action, {
        endpoint: "api/v1/user/user_id/team_expenses",
        destKey: "teamExpenses",
        successMessage: "Team expenses fetched successfully!",
      }),

    getUserExpensesComment: (state, action) => {
      setFetchState(state, action, {
        destKey: "userExpensesComment",
        successMessage: "User expenses comments fetched",
      });
    },

    savingOpportunity: (state, action) => {
      setFetchState(state, action, {
        destKey: "savingOpportunityData",
      });
    },

    similarServices: (state, action) => {
      setFetchState(state, action, {
        destKey: "similarServiceData",
      });
    },

    // updateFeedbacks: (state, action) =>{
    //   setFetchState(state, action, {
    //     destKey: "feedbacksData",
    //     endpoint: "api/v1/personal/feedbacks",
    //     successMessage: "Feedbacks fetched successfully",
    //   });
    // },

    // ------------------ special-case / status-based reducers ------------------

    // updateEmployees had special logic for status === 500
    updateEmployees(state, action) {
      state.endPoint = "api/v1/personal/employees";
      state.isLoading = false;

      if (!action.payload) {
        state.success = false;
        return;
      }

      // preserve original behaviour: if status === 500 show toast.error
      if (action.payload.status === 500) {
        toast.error(action.payload.error || "Internal Server Error");
        state.success = false;
        return;
      }

      state.success = true;
      state.message = "Employees fetched successfully";
      state.employeesData = action.payload.employees;
      state.managerName = action.payload.manager_name;
      state.totalTeamCost = action.payload.total_team_cost;
      state.directTeamCost = action.payload.direct_team_cost;
      state.averageTeamCost = action.payload.average_team_cost;
      state.totalSubmissions = action.payload.total_submissions;
      state.totalFeedbacks = action.payload.total_feedbacks;
      state.totalRequests = action.payload.total_requests;
      state.highCostServices = action.payload.high_cost_service;
    },

    // status-based actions using handleApiResponse
    createRequestComment: (state, action) =>
      handleApiResponse(state, action, {
        endpoint: "api/v1/personal/requests/{id}/comment",
        successMessage: "Request comment created successfully",
        dataKey: "requestCommentData",
        defaultFailMessage: "Request comment creation failed",
        successStatuses: [201],
      }),

    createSubmissionComment: (state, action) =>
      handleApiResponse(state, action, {
        endpoint: "api/v1/personal/submissions/{id}/comment",
        successMessage: "Submission comment created successfully",
        dataKey: "reCommentData",
        defaultFailMessage: "Submission comment creation failed",
        successStatuses: [201],
      }),

    createExpensesComment: (state, action) =>
      handleApiResponse(state, action, {
        endpoint: "/api/v1/expenses/id/comments",
        successMessage: "Expenses comment created successfully",
        dataKey: "expensesCommentData",
        defaultFailMessage: "Expenses comment creation failed",
        successStatuses: [201],
      }),

    updaterequestData: (state, action) =>
      handleApiResponse(state, action, {
        endpoint: "api/v1/personal/requests/id",
        successMessage: "Request updated successfully",
        dataKey: "requestDetailsData",
        defaultFailMessage: "Update Request failed",
        successStatuses: [200, 201],
      }),

    // createServiceRequest had extra behavior (storing updaterequestsData)
    createServiceRequest(state, action) {
      handleApiResponse(state, action, {
        endpoint: "api/v1/personal/requests",
        successMessage: "Request created successfully",
        dataKey: "updaterequestsData",
        defaultFailMessage: "Request Failed",
        successStatuses: [201],
      });

      const status = action?.payload?.status;
      if (status === 201) {
        // if API returned full payload, ensure updaterequestsData is set (original behaviour)
        state.updaterequestsData = action.payload;
        state.error = false;
      }
    },

    createFeedback: (state, action) =>
      handleApiResponse(state, action, {
        endpoint: "api/v1/personal/feedbacks",
        successMessage: "Feedback created successfully",
        dataKey: "updatefeedbackData",
        defaultFailMessage: "Feedback creation failed",
        successStatuses: [201],
      }),

    // createSubmission needs extra local updates (reset updateSubmissionsData + createdSubmission)
    createSubmission(state, action) {
      const response = action.payload;
      const submission = response?.data?.submission;

      handleApiResponse(state, action, {
        endpoint: "api/v1/personal/submissions",
        successMessage: "Submission created successfully",
        dataKey: "submissionsData",
        defaultFailMessage: "Submission creation failed",
        successStatuses: [201],
        onSuccess: () => {
          state.updateSubmissionsData = {
            expensed_by_name: null,
            service_to_replace_id: null,
            submission_details: "",
          };

          state.createdSubmission = true;
        },
      });
    },

    // update status-like reducers where payload contains `.data`
    updateStatus(state, action) {
      if (action.payload) {
        state.success = true;
        state.endPoint = "api/v1/personal/requests/{id}";
        state.message = "Status updated successfully";
        state.isLoading = false;
        state.updateStatusRequest = action.payload.data;
      } else {
        state.success = false;
        state.endPoint = "api/v1/personal/requests/{id}";
        state.isLoading = false;
      }
    },

    updateSubmissionsstatus(state, action) {
      if (action.payload) {
        state.success = true;
        state.endPoint = "api/v1/personal/submissions/{id}";
        state.message = "Status updated successfully";
        state.isLoading = false;
        state.updateStatusSubmission = action.payload.data;
      } else {
        state.success = false;
        state.endPoint = "api/v1/personal/submissions/{id}";
        state.isLoading = false;
      }
    },

    // approval and procurement:
    fatchApprovalRequests(state, action) {
      setFetchState(state, action, {
        endpoint: "api/v1/approval_requests",
        destKey: "approvalRequestsData",
      });
    },

    updateApprovalRequest(state, action) {
      // original behaviour: assign payload and show toast on success
      if (action.payload) {
        state.updateApprovalRequestData = action.payload;
        state.success = true;
        state.endPoint = "api/v1/approval_requests/:id";
        state.message = "Approval Request Status updated successfully";
        showSuccessToast(state.message);
        state.isLoading = false;
      } else {
        state.success = false;
        state.endPoint = "api/v1/approval_requests/:id";
        state.isLoading = false;
      }
    },

    createProcurement(state, action) {
      handleApiResponse(state, action, {
        endpoint: "/api/v1/procurement_requests",
        successMessage: "Procurement Requests created successfully",
        defaultFailMessage: "Request creation failed",
        successStatuses: [200, 201],
      });
    },

    // updateHistory(state, action) {}, // already defined earlier via setFetchState; included for compatibility

    // ------------------ small local setters / util reducers ------------------
    updatedPersonalRequests(state, action) {
      if (state.requestsData) {
        state.requestsData.requests = action.payload;
      }
    },

    updatedServices(state, action) {
      if (!state.servicesData) state.servicesData = {};
      state.servicesData.services = action.payload;
    },

    updatedTeam(state, action) {
      state.employeesData = action.payload;
    },

    updateSubmissionData(state, action) {
      state.updateSubmissionsData[action.payload.field] = action.payload.value;
    },

    updateSubmissionSortList(state, action) {
      if (!state.submissionsData) state.submissionsData = {};
      state.submissionsData.submissions = action.payload;
    },

    setCreatedSubmissionFalse(state) {
      state.createdSubmission = false;
    },

    resetUpdateSubmissionData(state) {
      state.updateSubmissionsData = {
        expensed_by_name: null,
        service_to_replace_id: null,
        submission_details: "",
      };
    },

    setLoading(state, action) {
      state.isLoading = action.payload;
    },

    setEmpty(state) {
      state.updaterequestsData = {};
    },

    setEmptyUsageReport(state) {
      state.usageReportData = {};
    },

    setEmptyEmployeeData(state) {
      state.employeeDetailData = undefined;
    },

    setEmptyEmployees(state) {
      state.employeesData = undefined;
      state.managerName = "";
      state.totalTeamCost = "";
      state.directTeamCost = "";
      state.averageTeamCost = undefined;
      state.totalSubmissions = "";
      state.totalFeedbacks = "";
      state.totalRequests = "";
      state.highCostServices = undefined;
    },

    updateTeamName(state, action) {
      state.teamName = action.payload;
    },

    setTeamId(state, action) {
      state.teamId = action.payload;
    },

    updatePassword: (state, action) =>
      handleApiResponse(state, action, {
        endpoint: "/api/v1/personal/users/user_id/update_password",
        successMessage: "Password updated successfully",
        defaultFailMessage: "Update password failed",
        // override error messages specifically for this endpoint
        customErrors: {
          404: "Old Password is incorrect",
        },
      }),

    // keep getters that were used by thunks
    // updateTeamExpenses(state, action) {
    //   setFetchState(state, action, {
    //     endpoint: "api/v1/user/:user_id/team_expenses",
    //     destKey: "teamExpenses",
    //     successMessage: "Team expenses fetched successfully!",
    //   });
    // },

    // keep placeholders for any other reducers referenced in exports
    // createFeedback: (state, action) => {}, // actual implementation above (kept for export consistency)
  },
});

// Export all named actions exactly as before to avoid breaking imports
export const {
  updateHistory,
  updaterequestData,
  updateSubmissionData,
  updateSubmissionSortList,
  updateFeedbackDetails,
  updateEmployees,
  updateServices,
  updateRequests,
  updateFeedbacks,
  updateSubmissionDetails,
  createServiceRequest,
  createFeedback,
  updateUsageReport,
  updateUsageReportHistory,
  updateRequestDetails,
  updateStatus,
  updateSubmissionsstatus,
  createRequestComment,
  createSubmissionComment,
  createExpensesComment,
  updatedTeam,
  updatedServices,
  updatedPersonalRequests,
  updateCategories,
  updateAllServices,
  updateEmployeeCostHistory,
  updateUserExpenses,
  createSubmission,
  updateSubmissions,
  updateEmployeeDetail,
  setLoading,
  setEmpty,
  setEmptyUsageReport,
  setEmptyEmployeeData,
  setEmptyEmployees,
  updateTeamName,
  savingOpportunity,
  similarServices,
  setCreatedSubmissionFalse,
  getUserExpensesComment,
  setTeamId,
  updateTeamExpenses,
  printingUsageReport,
  teamPrintingUsageReport,
  updatePassword,
  fatchApprovalRequests,
  createProcurement,
  updateApprovalRequest,
  updateNotifications,
  markAllReadNotification,
  markreadNotification,
  userSettings,
} = personalDashboardSlice.actions;

export const selectPersonalDashboard = (state) => state.personalDashboard;

export const getEmployeesRequest = (userId) => (dispatch) => {
  fetchWithToken(`/api/v1/personal/users/${userId}/employees`, dispatch, updateEmployees);
};

export const getCategories = () => (dispatch) => {
  fetchWithToken("api/v1/categories", dispatch, updateCategories);
};

export const getAllServices = () => (dispatch) => {
  fetchWithToken("api/v1/personal/services/all_services", dispatch, updateAllServices);
};

export const createRequestComments = (id, requestData) => (dispatch) => {
  postWithToken(`api/v1/personal/requests/${id}/comment`, requestData, dispatch, createRequestComment);
};

export const createSubmissionComments = (id, requestData) => (dispatch) => {
  postWithToken(`api/v1/personal/submissions/${id}/comment`, requestData, dispatch, createSubmissionComment);
};

export const createExpensesComments = (id, requestData) => (dispatch) => {
  postWithToken(`/api/v1/expenses/${id}/comments`, requestData, dispatch, createExpensesComment);
};

export const getUserExpensesComments = (id, userId) => (dispatch) => {
  fetchWithToken(`/api/v1/expenses/${id}/comments?user_id=${userId}`, dispatch, getUserExpensesComment);
};


export const updateRequest = (id, requestData) => (dispatch) => {
  postWithToken(`api/v1/personal/requests/:${id}`, requestData, dispatch, updaterequestData);
};

export const getSubmissionDetailsRequest = (id, userId) => (dispatch) => {
  fetchWithToken(`api/v1/personal/submissions/${id}?user_id=${userId}`, dispatch, updateSubmissionDetails);
};

export const getHistoryRequest = (billing_period_id = null) => (dispatch) => {
  fetchWithToken(`api/v1/personal/costs_history?billing_period_id=${billing_period_id}`, dispatch, updateHistory);
};

export const getServicesRequest = (billing_period_id = null) => (dispatch) => {
  fetchWithToken(`api/v1/personal/services?billing_period_id=${billing_period_id}`, dispatch, updateServices);
};

export const createServicesRequest = (requestData) => (dispatch) => {
  postWithToken("api/v1/personal/requests", requestData, dispatch, createServiceRequest);
};

export const getUsageReportRequest = (serviceId, customBillingPeriodId = null) => (dispatch) => {
  fetchWithToken(`api/v1/personal/services/${serviceId}/usage_report?customBillingPeriodId=${customBillingPeriodId}`, dispatch, updateUsageReport);
};

export const getPrintingUsageReport = (serviceId, customBillingPeriodId) => (dispatch) => {
  fetchWithToken(`api/v1/personal/services/${serviceId}/usage_report_printing?customBillingPeriodId=${customBillingPeriodId}`, dispatch, printingUsageReport);
};

export const getTeamPrintingUsageReport = (serviceId, userId) => (dispatch) => {
  fetchWithToken(`/api/v1/personal/users/${userId}/team_printing_usage_report?service_id=${serviceId}`, dispatch, teamPrintingUsageReport);
};

export const getUsageReportHistory = (service_id) => (dispatch) => {
  fetchWithToken(`api/v1/personal/services/${service_id}/costs_history`, dispatch, updateUsageReportHistory);
};

export const getFeedBackDetails = (serviceID, feedbackID) => (dispatch) => {
  fetchWithToken(`/api/v1/personal/services/${serviceID}/usage_report?feedback_id=${feedbackID}`, dispatch, updateFeedbackDetails);
};

export const getcompanyFeedBackDetails = (serviceID, feedbackID) => (dispatch) => {
  fetchWithToken(`/api/v1/company/services/${serviceID}/usage_report?feedback_id=${feedbackID}`, dispatch, updateFeedbackDetails);
};

export const getFeedBackDetailsCompany = (id, feedBackId) => (dispatch) => {
  fetchWithToken(`/api/v1/company/employees/${id}/feedbacks?feedback_id=${feedBackId}`, dispatch, updateFeedbackDetails);
};

export const createFeedbackRequest = (requestData) => (dispatch) => {
  postWithToken("api/v1/personal/feedbacks", requestData, dispatch, createFeedback);
};

export const getRequestDetails = (id) => (dispatch) => {
  fetchWithToken(`api/v1/personal/requests/${id}`, dispatch, updateRequestDetails);
};

export const statusUpdateRequest = (id, requestData) => (dispatch) => {
  patchWithToken(`api/v1/personal/requests/${id}`, requestData, dispatch, updateStatus);
};

export const statusUpdateSubmission = (id, submissionData) => (dispatch) => {
  patchWithToken(`api/v1/personal/submissions/${id}`, submissionData, dispatch, updateSubmissionsstatus);
};

export const getSubmissionsRequest = () => (dispatch) => {
  fetchWithToken("api/v1/personal/submissions", dispatch, updateSubmissions);
};

export const getRequestsPage = (sort_by = "request_type", sort_direction = "asc") => (dispatch) => {
  fetchWithToken(`api/v1/personal/requests?sort_by=${sort_by}&sort_direction=${sort_direction}`, dispatch, updateRequests);
};

export const getRequestsPagewithoutParams = () => (dispatch) => {
  fetchWithToken("api/v1/personal/requests", dispatch, updateRequests);
};

export const getSavingOpportunity = (id, queryType) => (dispatch) => {
  fetchWithToken(`api/v1/savings_opportunities?${queryType}=${id}`, dispatch, savingOpportunity);
};

export const createSubmissionRequest = (requestData) => (dispatch) => {
  postWithToken("api/v1/personal/submissions", requestData, dispatch, createSubmission);
};

export const getFeedbacksRequest = () => (dispatch) => {
  fetchWithToken("api/v1/personal/feedbacks", dispatch, updateFeedbacks);
};

export const getEmployeeDetailRequest = (id) => (dispatch) => {
  fetchWithToken(`api/v1/personal/employees/${id}`, dispatch, updateEmployeeDetail);
};

export const getEmployeeCostHistoryRequest = (emp_id) => (dispatch) => {
  fetchWithToken(`api/v1/employees/${emp_id}/costs_history`, dispatch, updateEmployeeCostHistory);
};

export const getUserExpenses = (emp_id, customBillingPeriodId = null) => (dispatch) => {
  fetchWithToken(`api/v1/user/${emp_id}/personal_expenses?customBillingPeriodId=${customBillingPeriodId}`, dispatch, updateUserExpenses);
};

export const getTeamExpenses = (emp_id) => (dispatch) => {
  fetchWithToken(`api/v1/user/${emp_id}/team_expenses`, dispatch, updateTeamExpenses);
};

export const updateUserPasswordRequest = (user_id, userData) => (dispatch) => {
  patchWithToken(`/api/v1/personal/users/${user_id}/update_password`, userData, dispatch, updatePassword);
};

export const getApprovalRequests = () => (dispatch) => {
  fetchWithToken(`/api/v1/approval_requests`, dispatch, fatchApprovalRequests);
};

export const createProcurementRequest = (requestData) => (dispatch) => {
  postWithToken("api/v1/procurement_requests", requestData, dispatch, createProcurement);
};

export const updateApprovalRequestStatus = (id, requestData) => (dispatch) => {
  patchWithToken(`/api/v1/approval_requests/${id}`, requestData, dispatch, updateApprovalRequest);
};

export const getNotifications = () => (dispatch) => {
  fetchWithToken("api/v1/personal/notifications", dispatch, updateNotifications);
};

export const markAllReadNotifications = () => (dispatch) => {
  postWithToken("api/v1/personal/notifications/mark_all_read",{}, dispatch, markAllReadNotification);
};

export const markReadNotification = (id) => (dispatch) => {
  postWithToken(`api/v1/personal/notifications/${id}/mark_read`,{}, dispatch, markreadNotification);
};

export const updateUserSettings = (user_id, userData) => (dispatch) => {
  patchWithToken(`/api/v1/personal/users/${user_id}/update_settings`, userData, dispatch, userSettings);
};

export default personalDashboardSlice.reducer;
